import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Profit Maximization) ---
import {
    // Core Concepts & General
    faChartLine,        // Profit Growth / Revenue / Strategy / Analysis
    faDollarSign,       // Profit / Revenue / Cost Control / Billing / Budget
    faTasks,            // Operational Efficiency / Workflow / Resource Utilization
    faLightbulb,        // Strategy / Innovation / Solutions
    faCheckCircle,      // Quality / Success / Improvement
    // 10 Strategic Points
    faSearchDollar,     // 1. Identifying High-Profit Areas
    faLaptopCode,       // 2. Harnessing Technology
    faUsersCog,         // 3. Maximizing Resource Utilization (People/Equipment)
    faSmileBeam,        // 4. Enhancing Patient Satisfaction & Loyalty
    faFileInvoiceDollar,// 5. Effective Cost Control Measures
    faSitemap,          // 6. Diversifying Revenue Streams
    faReceipt,          // 7. Optimizing Billing & RCM
    faStream,           // 8. Streamlining Operational Workflow
    faBullhorn,         // 9. Robust Brand & Marketing Strategy
    faSyncAlt,          // 10. Long-Term Sustainability & Continuous Improvement
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faBoxesStacked, faWarehouse, faClipboardList, faHeartPulse, faBolt, faDatabase,
    faSearch, faBalanceScale, faChartBar, faCalendarAlt, faShoppingCart, faCalculator,
    faChalkboardTeacher, faMicroscope, faTools, faCog, faHandshake, faWeightHanging,
    faWrench, faNetworkWired, faComments, faUserTie, faLifeRing, faUserCheck, faGlobe,
    faLeaf, faExpandArrowsAlt, faBiohazard, faAward, faProjectDiagram, faPalette,
    faDirections, faRulerCombined, faBuilding
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Title for the 10 points section

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for profit/finance/strategy
    const heroBgImage = 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Analysis/charts image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Profit Maximization: 10 Strategic Points</h1>
            <p className="cliniqor-sub-heading">
              Driving Healthcare Revenue Growth with Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Profit maximization is a strategic necessity. Cliniqor leverages cutting-edge strategies, tech, and data insights to optimize profit potential while maintaining high care standards, balancing cost control with operational excellence.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* No separate intro section needed, Hero covers it */}

        {/* --- 10 STRATEGIC POINTS (Detailed Planning Section - 10 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>10 Effective Strategies for Profit Maximization</h2>
               <p>The bedrock principles for enhancing financial health in healthcare facilities.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 10 Points */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSearchDollar} /></div>
                    <div className="cliniqor-content"><h4>1. Identifying High-Profit Areas via Analysis</h4> <p>Uncovering top revenue-generating departments/services for strategic resource allocation.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLaptopCode} /></div>
                    <div className="cliniqor-content"><h4>2. Harnessing Technology for Optimization</h4> <p>Integrating tech (Analytics, HMS, AI) to streamline operations, reduce costs, improve accuracy.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsersCog} /></div>
                    <div className="cliniqor-content"><h4>3. Maximizing Resource Utilization</h4> <p>Auditing and optimizing staff, equipment, and space usage for peak efficiency.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSmileBeam} /></div>
                    <div className="cliniqor-content"><h4>4. Enhancing Patient Satisfaction & Loyalty</h4> <p>Improving care experiences through personalization, communication, and feedback systems.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faFileInvoiceDollar} /></div>
                    <div className="cliniqor-content"><h4>5. Implementing Effective Cost Control</h4> <p>Auditing costs, renegotiating contracts, optimizing labor, and reducing waste.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSitemap} /></div>
                    <div className="cliniqor-content"><h4>6. Diversifying Revenue Streams</h4> <p>Introducing new services like telemedicine, wellness programs, and specialized packages.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faReceipt} /></div>
                    <div className="cliniqor-content"><h4>7. Optimizing Billing & RCM</h4> <p>Streamlining billing, improving collections, managing claims, and ensuring accurate revenue capture.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faStream} /></div>
                    <div className="cliniqor-content"><h4>8. Streamlining Operational Workflow</h4> <p>Identifying bottlenecks, re-engineering processes, optimizing scheduling, improving patient flow.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBullhorn} /></div>
                    <div className="cliniqor-content"><h4>9. Developing Robust Brand & Marketing</h4> <p>Building brand awareness, using targeted digital marketing, and engaging the community.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSyncAlt} /></div>
                    <div className="cliniqor-content"><h4>10. Establishing Long-Term Sustainability</h4> <p>Focusing on continuous improvement, innovation, tech integration, and periodic strategy reviews.</p> </div>
                 </div>
            </div>
          </div>
        </section>


       {/* --- CONCLUSION SECTION (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div className="cliniqor-section-title cliniqor-fade-in-up"> {/* No specific ref needed */}
                 <h2>Maximizing Profit in a Competitive Healthcare Market</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Profit maximization is a holistic approach optimizing every organizational aspect. Cliniqor’s expertise in strategic planning, technology, operational efficiency, and patient-centered care helps increase profitability without compromising quality.</p>
                   <p>By implementing these 10 strategic points, providers enhance competitiveness, streamline operations, and create sustainable profit models. We craft tailored strategies meeting unique facility needs, ensuring every aspect contributes to maximizing profits and improving patient outcomes for a prosperous future.</p>
              </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Unlock Your Healthcare Facility's Full Profit Potential</h3>
          <p>Let Cliniqor help your organization implement these strategies to enhance profitability, streamline operations, and ensure long-term financial health and success. Contact us today!</p>
          <a href="#contact" className="cliniqor-cta-button">Request Profit Maximization Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;